import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import usePiperFeatures from '../../hooks/usePiperFeatures';
import PiperTermsModal from './PiperTermsModal';

const PiperConsentContext = createContext({
  requireConsent: () => {},
  openTermsModal: () => {},
  consentRequired: false,
  declined: false,
});

/**
 * Intercepts Piper AI entry points: run the action when terms are accepted,
 * open the terms modal and stash the action when consent is still required,
 * and no-op after a decline.
 */
export const PiperConsentProvider = ({ children }) => {
  const { consentRequired, declined, effectiveEnabled } = usePiperFeatures();
  const [modalOpen, setModalOpen] = useState(false);
  const pendingRef = useRef(null);

  const openTermsModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const requireConsent = useCallback((fn) => {
    if (effectiveEnabled) {
      fn?.();
      return;
    }
    if (declined) return;
    pendingRef.current = typeof fn === 'function' ? fn : null;
    setModalOpen(true);
  }, [effectiveEnabled, declined]);

  const handleAccepted = useCallback(() => {
    // Close the modal; the pending action runs only after piper-access
    // refetches and effectiveEnabled becomes true (chat loanId wires up).
    setModalOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setModalOpen(false);
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    if (!effectiveEnabled) return;
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    pending();
  }, [effectiveEnabled]);

  const value = useMemo(
    () => ({ requireConsent, openTermsModal, consentRequired, declined }),
    [requireConsent, openTermsModal, consentRequired, declined],
  );

  return (
    <PiperConsentContext.Provider value={value}>
      {children}
      <PiperTermsModal open={modalOpen} onAccepted={handleAccepted} onClose={handleClose} />
    </PiperConsentContext.Provider>
  );
};

export const usePiperConsent = () => useContext(PiperConsentContext);

export default PiperConsentContext;
